import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { PlatformIcon, useLinkPlatforms } from '../profile/links';
import { Field } from '../../components/ui/Field';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';
import type { Profile } from '../profile/types';
import { useFormErrors } from '../auth/useFormErrors';
import { EmojiButton } from '../../components/ui/EmojiPicker';
import { useEmojiComposer } from '../../lib/emoji';
import { AvatarPicker } from './AvatarPicker';
import { BannerPicker } from './BannerPicker';
import { PendingCredits } from './PendingCredits';


export function SettingsPage({ inDrawer = false }: { inDrawer?: boolean } = {}) {
  const user = useSession((s) => s.user);
  const refreshUser = useSession((s) => s.refreshUser);
  const queryClient = useQueryClient();
  const errors = useFormErrors();

  const { data } = useQuery({
    queryKey: ['profile', user?.username],
    queryFn: () => api.get<Profile>(`/users/${user?.username}`),
    enabled: Boolean(user?.username),
  });

  const [form, setForm] = useState({ display_name: '', bio: '', location: '' });

  // The bio is the only prose field here, so it is the only one that gets a
  // picker. A location or a display name does not need one.
  const emoji = useEmojiComposer(form.bio, (bio) => setForm((current) => ({ ...current, bio })));
  const [links, setLinks] = useState<{ platform: string; url: string }[]>([]);

  // The catalogue comes from the server, so the picker cannot offer a platform
  // the save would reject. See `LinkOptionsAction`.
  const { platforms, max } = useLinkPlatforms();
  const [saved, setSaved] = useState(false);

  /*
   * Seeded from the server once the profile arrives.
   *
   * Keyed on the profile id rather than the object, so a background refetch
   * that returns an identical profile does not blow away edits in progress.
   * That is the classic version of this bug: type a bio, a refetch lands, and
   * the field silently reverts.
   */
  useEffect(() => {
    if (!data) return;

    setForm({
      display_name: data.display_name,
      bio: data.bio ?? '',
      location: data.location ?? '',
    });
    setLinks(data.links.map((l) => ({ platform: l.platform, url: l.url })));
  }, [data?.id]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<Profile>('/me', {
        ...form,
        links: links.filter((l) => l.platform && l.url.trim()),
      }),
    onSuccess: async () => {
      setSaved(true);
      errors.clear();
      await refreshUser();
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error) => {
      setSaved(false);
      errors.capture(error);
    },
  });

  if (!user) return null;

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    save.mutate();
  }

  return (
    <div
      className={
        inDrawer
          ? 'flex flex-col gap-5 p-5'
          : 'mx-auto flex max-w-2xl flex-col gap-6 px-6 py-10'
      }
    >
      {/* The drawer's own header already says what this is, and repeating it
          would push the first control below the fold. */}
      {!inDrawer && (
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="mt-1 text-sm text-soft">How you appear to other people.</p>
        </div>
      )}

      {/*
        First, above everything else, and only when there is something to answer.
        A pending credit is somebody waiting on you - it does not belong below
        the fold under your own profile fields.
      */}
      <PendingCredits />

      <Card className="p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-faint">Picture</h2>
        <AvatarPicker currentUrl={data?.avatar?.medium} displayName={user.display_name} />
      </Card>

      {/*
        After the picture, because it is the larger surface but the less
        personal one - and because most people will look at it, see it already
        has a gradient, and move on.
      */}
      <Card className="p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-faint">Header</h2>
        <BannerPicker gradient={data?.banner_gradient} imageUrl={data?.banner} />
      </Card>

      {/*
        After the pictures and before the profile form, because it is a
        preference rather than a field - it changes what happens to you, not
        what other people see.
      */}
      <Card className="p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-faint">
          Notifications
        </h2>
        <LiveNotices />
      </Card>

      <Card className="p-6">
        <form onSubmit={submit} className="flex flex-col gap-4">
          {errors.message && <Alert tone="error">{errors.message}</Alert>}
          {saved && <Alert tone="success">Saved.</Alert>}

          <Field
            label="Display name"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            error={errors.fields.display_name}
            maxLength={60}
          />

          <div className="flex flex-col gap-1.5">
            <label htmlFor="bio" className="text-sm font-medium text-soft">
              Bio
            </label>
            <textarea
              id="bio"
              ref={emoji.ref as React.RefObject<HTMLTextAreaElement>}
              rows={4}
              value={form.bio}
              maxLength={500}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              aria-invalid={errors.fields.bio ? true : undefined}
              className={`rounded-[var(--radius-control)] border bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint ${
                errors.fields.bio ? 'border-negative' : 'border-line focus:border-line-strong'
              }`}
            />
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2">
                <EmojiButton onSelect={emoji.insert} direction="down" align="left" />
                <span className="text-negative">{errors.fields.bio}</span>
              </span>
              {/* Counted down rather than up: what matters is how much room is
                  left, and it only earns attention near the limit. */}
              <span className={form.bio.length > 450 ? 'text-warning' : 'text-faint'}>
                {500 - form.bio.length}
              </span>
            </div>
          </div>

          <Field
            label="Location"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            error={errors.fields.location}
            maxLength={80}
            placeholder="Optional"
          />

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-soft">Links</span>
            {errors.fields.links && <span className="text-xs text-negative">{errors.fields.links}</span>}

            {links.map((link, index) => {
              const platform = platforms.find((p) => p.platform === link.platform);

              /*
               * Every platform stays selectable except the ones already used,
               * and this row's own always does - otherwise the select would
               * have no valid value for what it is currently showing.
               *
               * `website` is exempt: two different sites are two different
               * things, which the named platforms never are.
               */
              const available = platforms.filter(
                (p) =>
                  p.platform === link.platform ||
                  p.platform === 'website' ||
                  !links.some((l) => l.platform === p.platform),
              );

              return (
                <div key={index} className="flex flex-wrap items-start gap-2">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-soft">
                      <PlatformIcon platform={link.platform} />
                    </span>
                    <select
                      aria-label={`Link ${index + 1} platform`}
                      value={link.platform}
                      data-link-platform={index}
                      onChange={(e) =>
                        setLinks(links.map((l, i) => (i === index ? { ...l, platform: e.target.value } : l)))
                      }
                      className="w-44 appearance-none rounded-[var(--radius-control)] border border-line bg-app py-2 pl-9 pr-3 text-sm text-ink"
                    >
                      {available.map((p) => (
                        <option key={p.platform} value={p.platform}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex min-w-[12rem] flex-1 flex-col gap-1">
                    <input
                      aria-label={`Link ${index + 1} URL`}
                      /* The host the server will check for, said before the
                         save rather than after it. */
                      placeholder={
                        platform && platform.hosts.length > 0
                          ? `https://${platform.hosts[0]}/…`
                          : 'https://…'
                      }
                      value={link.url}
                      maxLength={200}
                      onChange={(e) =>
                        setLinks(links.map((l, i) => (i === index ? { ...l, url: e.target.value } : l)))
                      }
                      className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm"
                    />
                    {platform && platform.hosts.length > 0 && (
                      <span className="font-mono text-[10px] text-ghost">
                        must point at {platform.hosts.join(' or ')}
                      </span>
                    )}
                  </div>

                  <Button variant="ghost" onClick={() => setLinks(links.filter((_, i) => i !== index))}>
                    Remove
                  </Button>
                </div>
              );
            })}

            {links.length < max && platforms.length > 0 && (
              <Button
                variant="secondary"
                data-add-link
                onClick={() => {
                  // The first platform they have not used yet, so adding a
                  // second link does not land on a duplicate they must then fix.
                  const next =
                    platforms.find((p) => !links.some((l) => l.platform === p.platform)) ??
                    platforms[platforms.length - 1]!;

                  setLinks([...links, { platform: next.platform, url: '' }]);
                }}
              >
                Add a link
              </Button>
            )}
          </div>

          <Button type="submit" loading={save.isPending}>
            Save changes
          </Button>
        </form>
      </Card>
    </div>
  );
}

/**
 * Whether to hear about live rooms.
 *
 * ## Why the default is on
 *
 * Following a host is already an opt-in to hearing about them, and going live
 * is the thing they do. Defaulting this off would mean the feature existed for
 * whoever happened to find this switch.
 *
 * ## Why one switch and not one per host
 *
 * A per-host mute is the obvious next step and deliberately not the first one.
 * Frequency is handled on the other side - `AnnounceLive` debounces per station,
 * so a host whose connection drops does not notify anybody twice - which is the
 * problem people actually have. A list of forty toggles solves a problem nobody
 * has yet.
 */
function LiveNotices() {
  const user = useSession((s) => s.user);
  const refreshUser = useSession((s) => s.refreshUser);
  const on = user?.notify_live ?? true;

  const update = useMutation({
    mutationFn: (notify_live: boolean) => api.patch('/me', { notify_live }),
    // The switch reads from the session, so it has to be re-read rather than
    // set locally - otherwise it snaps back on the next page load.
    onSuccess: () => refreshUser(),
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-ink">When a host I follow goes live</span>

        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label="Notify me when a host I follow goes live"
          data-notify-live
          disabled={update.isPending}
          onClick={() => update.mutate(!on)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            on ? 'border-transparent bg-accent' : 'border-line bg-raised'
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              on ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <p className="text-xs text-faint">
        {on
          ? 'You will get one notification per broadcast. A host who drops and reconnects will not send a second.'
          : 'You will not be told when hosts you follow go on air.'}
      </p>
    </div>
  );
}
